import React from "react";
import cx from "classnames";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import { Droppable } from "react-beautiful-dnd";
import Typography from "@material-ui/core/Typography";
import DraggableInitiativeCard from "./DraggableInitiativeCard";
import { grayColor } from "../../assets/jss/material-dashboard-pro-react";

const useStyles = makeStyles(() => ({
  boardColumn: {
    borderRadius: 6,
    padding: "5px 0 10px",
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    transition: "opacity 0.3s ease",
    backgroundColor: grayColor[8],
  },
  boardColumnOpportunity: {
    backgroundColor: "#fff",
    backgroundImage: `repeating-linear-gradient(135deg, ${grayColor[19]}bb 0, ${grayColor[19]}bb 10px, ${grayColor[19]} 10px, ${grayColor[19]} 20px)`,
  },
  faded: {
    opacity: 0.3,
  },
  stageHeader: {
    textAlign: "center",
    margin: "0 -6px 1px",
    color: "#555555",
    fontWeight: 400,
    fontSize: "12px",
    textTransform: "uppercase",
  },
  stageSummary: {
    display: "flex",
    justifyContent: "center",
    gap: 3,
    margin: "0 0 10px",
    padding: "0 30px",
    flexWrap: "wrap",
    backgroundColor: grayColor[10],
  },
  cardsContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "15px",
    flex: 1,
    overflowY: "auto",
    padding: "0 6px",
    "&::-webkit-scrollbar": {
      width: "4px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: grayColor[6],
      borderRadius: "4px",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "transparent",
    },
  },
  emptyText: {
    color: "#aaa",
    textAlign: "center",
    fontStyle: "italic",
    fontSize: "10px",
    marginTop: "5px",
  },
}));

const DroppableStageColumn = ({
  stageKey,
  stageItems,
  stageSummary,
  newFilters,
  statusOrder,
  loadingInitiativeId,
  handleInitiativeClick,
  t,
  StageNumber,
  selectedInitiativeId = null,
}) => {
  const hasSelection = selectedInitiativeId !== null;
  const classes = useStyles();
  const isFilteredOut =
    newFilters.stage !== null && newFilters.stage !== stageKey;

  return (
    <Droppable droppableId={stageKey} key={stageKey}>
      {(provided) => (
        <div
          className={cx(classes.boardColumn, {
            [classes.boardColumnOpportunity]: stageKey === "opportunity",
            [classes.faded]: isFilteredOut,
          })}
          ref={provided.innerRef}
          {...provided.droppableProps}
        >
          {/* Stage Header */}
          <div className={classes.stageHeader}>
            {t(`initiatives.stages.${stageKey}`)}
          </div>

          {/* ───────────  STAGE SUMMARY ─────────── */}
          <div className={classes.stageSummary}>
            <StageNumber
              label={t("initiatives.total")}
              value={stageSummary[stageKey].count}
            />
            <StageNumber
              grow
              label={t("initiatives.benefit_global")}
              value={`${stageSummary[stageKey].global_benefit.toFixed(1)} p.p`}
            />
          </div>

          {/* Initiative Cards */}
          <div className={classes.cardsContainer}>
            {stageItems.length === 0 ? (
              <Typography variant="caption" className={classes.emptyText}>
                {t("initiatives.no_initiatives_here")}
              </Typography>
            ) : (
              [...stageItems]
                .sort((a, b) => {
                  const sA = statusOrder[a.status] || 99;
                  const sB = statusOrder[b.status] || 99;
                  return sA - sB;
                })
                .map((item, index) => (
                  <DraggableInitiativeCard
                    key={item.id}
                    item={item}
                    index={index}
                    loadingInitiativeId={loadingInitiativeId}
                    handleInitiativeClick={handleInitiativeClick}
                    t={t}
                    stageKey={stageKey}
                    selectedInitiativeId={selectedInitiativeId}
                    hasSelection={hasSelection}
                  />
                ))
            )}
            {provided.placeholder}
          </div>
        </div>
      )}
    </Droppable>
  );
};

DroppableStageColumn.propTypes = {
  stageKey: PropTypes.string.isRequired,
  stageItems: PropTypes.array.isRequired,
  stageSummary: PropTypes.object.isRequired,
  newFilters: PropTypes.object.isRequired,
  statusOrder: PropTypes.object.isRequired,
  loadingInitiativeId: PropTypes.number,
  handleInitiativeClick: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  StageNumber: PropTypes.elementType.isRequired,
  selectedInitiativeId: PropTypes.number,
};

export default DroppableStageColumn;
